import { track } from '@amplitude/analytics-browser'
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import isEmpty from 'lodash.isempty'
import type { PropsWithChildren } from 'react'
import { useEffect, useState } from 'react'
import Confetti from 'react-confetti'

import CalendarPage from 'CalendarPage'
import type {
  AccountSelectorProps,
  ConversationProps,
  UpcomingPostCardProps,
  PostPreviewSectionProps,
  PostActionProps,
} from 'Components'
import {
  Conversation,
  Navbar,
  LoadingPlaceholder,
  PostAction,
  UpcomingPostCard,
  PostPreviewSection,
  EmptyUpcomingPosts,
  NoPostToApprove,
} from 'Components'
import type { NavbarTabView, TabViewValue } from 'Components/Navbar'
import InstagramGridPage from 'InstagramGridPage'
import { datePlaceholderForDraftPost } from 'normalizer'
import { Button } from 'ui/button'
import type { PreviewVariant } from 'utils/postPreviewVariant'

// SKE-3789: layout migrated styled-components → Tailwind. Behaviour held
// constant — grid template + 1280px breakpoint, per-column scroll, the
// sticky "Upcoming Posts" header, the `hidden` visibility toggling (now
// Tailwind's `hidden` util), and the delay-show column animation
// (keyframe `custom-show-animation` lives in index.css). The 1280px
// breakpoint uses an arbitrary `max-[1280px]:`/`min-[1280px]:` literal.

// "Upcoming Posts" heading (was the styled `Title` h4: 16px/700)
const TITLE_CLASS = 'font-gotham text-xl font-extrabold text-neutral-800'

// Centre column: overflow-x stays `auto` to match prior rendered behaviour —
// the old `overflow-x: auto !important` won over the inline `visible`, so the
// "PDF tooltip" override never actually applied. overflow-y auto for long
// previews.
const LEGACY_APPROVAL_GRID_CLASS =
  'mx-auto grid grid-cols-[1fr_440px_1fr] overflow-x-auto overflow-y-hidden max-[1280px]:grid-cols-[1fr_400px_1fr]'

const UPLIFT_APPROVAL_GRID_CLASS =
  'mx-auto grid grid-cols-[minmax(480px,1fr)_minmax(480px,auto)_minmax(480px,1fr)] overflow-x-auto overflow-y-hidden max-[1280px]:grid-cols-[minmax(300px,1fr)_minmax(440px,auto)_minmax(300px,1fr)]'

const LEGACY_PREVIEW_COLUMN_CLASS =
  'relative h-[calc(100vh-70px)] overflow-auto border-x border-neutral-100 bg-white px-10 pt-6 max-[1280px]:px-5'

const UPLIFT_PREVIEW_COLUMN_CLASS =
  'relative box-border h-[calc(100vh-70px)] min-w-[480px] overflow-auto border-x border-neutral-100 bg-white px-10 pt-6 max-[1280px]:min-w-[440px] max-[1280px]:px-5'

export const getApprovalGridClassName = (previewVariant: PreviewVariant) =>
  previewVariant === 'uplift'
    ? UPLIFT_APPROVAL_GRID_CLASS
    : LEGACY_APPROVAL_GRID_CLASS

// SKE-4166 (demo UX uplift): the desktop two-pane hands `App` a compact layout
// so the client area (list + preview) fits beside the "Your Sked" team pane at
// 1200px. Two columns instead of three; the Post History & Discussion column
// folds into a right-hand drawer. Demo-only — the default (three-column) path
// is untouched for real reviewers. Column heights subtract the ClientFrame
// browser-chrome bar (~34px) on top of the Navbar so the pair clears the fold.
const COMPACT_APPROVAL_GRID_CLASS =
  'grid w-full grid-cols-[minmax(300px,340px)_minmax(400px,1fr)] overflow-x-auto overflow-y-hidden'

const COMPACT_LIST_COLUMN_CLASS =
  'relative h-full overflow-y-auto overflow-x-hidden bg-[#fcfcfc] px-6 pb-6 pt-4'

const COMPACT_PREVIEW_CELL_CLASS =
  'relative box-border flex h-full min-w-[400px] flex-col overflow-hidden border-x border-neutral-100 bg-white'

export const getPreviewColumnClassName = (previewVariant: PreviewVariant) =>
  previewVariant === 'uplift'
    ? UPLIFT_PREVIEW_COLUMN_CLASS
    : LEGACY_PREVIEW_COLUMN_CLASS

interface AppProps {
  lastAction: string | null
  isLoading: boolean
  accountSelectorProps: AccountSelectorProps
  setting: {
    logoUrl: string
    companyName: string
  }
  upcomingPosts: {
    id: string
    date: string
    isVisible: boolean
    posts: (UpcomingPostCardProps & {
      isVisible?: boolean
      postIndex: number
    })[]
  }[]
  postPreviewSectionProps: PostPreviewSectionProps
  conversationProps: ConversationProps
  postActionProps: PostActionProps
  handleCardClick: (postRowId: string, postId: string, status: string) => void
  navigatePost: (postIndex: number) => void
  isReadOnly: boolean
  previewVariant: PreviewVariant
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  /**
   * SKE-4166 (demo UX uplift). Set ONLY by the demo's desktop two-pane wrapper.
   * Switches the approver to a two-column compact grid (list + preview) and
   * folds the Post History & Discussion column into a collapsible drawer that
   * auto-opens on "Request change". Default `false` = today's exact
   * three-column behaviour, so the real reviewer UI is byte-identical.
   */
  demoCompact?: boolean
}

function App({
  accountSelectorProps,
  lastAction,
  isLoading,
  setting,
  upcomingPosts,
  postPreviewSectionProps,
  conversationProps,
  postActionProps,
  handleCardClick,
  navigatePost,
  isReadOnly,
  previewVariant,
  updatePostState,
  demoCompact = false,
}: PropsWithChildren<AppProps>) {
  const [tabView, setTabView] = useState<TabViewValue>('approval')
  const [shouldShowConfetti, setShouldShowConfetti] = useState(false)
  // Demo-only: the Post History & Discussion drawer. Auto-opens when a change
  // is requested (that is the beat where the thread matters), and toggled by
  // the fa-comment control in the preview column.
  const [isDiscussionOpen, setIsDiscussionOpen] = useState(false)

  // In compact mode the request-change button must also open the discussion
  // drawer. Wrapping `postActionProps.updatePostState` keeps this local — no
  // callback threaded through Homepage — per the plan's "a local useState
  // opened by the request-change button" note.
  const compactPostActionProps: PostActionProps = demoCompact
    ? {
        ...postActionProps,
        updatePostState: (
          reviewKey: string,
          postId: string,
          finalState: string
        ) => {
          if (finalState === 'requestChange') {
            setIsDiscussionOpen(true)
          }
          postActionProps.updatePostState(reviewKey, postId, finalState)
        },
      }
    : postActionProps

  const renderTotalPendingCount = (posts: AppProps['upcomingPosts']) => {
    const flattenArr = posts.map((item) => item.posts).flat()

    let pendingCount = 0
    flattenArr.forEach((item) => {
      if (item.status === 'pending') {
        pendingCount += 1
      }
    })
    return pendingCount
  }

  const handlePendingCountClick = () => {
    const firstPendingPostIndex = upcomingPosts
      .flatMap((item) => item.posts)
      .findIndex((post) => post.status === 'pending')

    navigatePost(firstPendingPostIndex + 1)
  }

  // "View Pending" jumps to the first pending post, so disable it once that
  // post is already selected — clicking would be a no-op.
  const isFirstPendingSelected =
    upcomingPosts
      .flatMap((item) => item.posts)
      .find((post) => post.status === 'pending')?.isSelected ?? false

  // Trigger confetti when the reviewer clears the queue. Idempotent —
  // setShouldShowConfetti(true) is a no-op if already true (React
  // dedupes via Object.is), so a re-render mid-show doesn't restart
  // the timer or fire a second burst. Reading shouldShowConfetti
  // inside this effect would put us in an infinite loop once we add
  // it to deps and the timer flips it back to false (lastAction is
  // never cleared, so the conditions remain true), so the trigger
  // doesn't read it at all — separation of trigger vs. timer below.
  useEffect(() => {
    if (
      lastAction &&
      renderTotalPendingCount(upcomingPosts) === 0 &&
      ['approved', 'submitChangeDetail'].includes(lastAction)
    ) {
      // Honour the OS-level reduced-motion preference for vestibular-
      // sensitive reviewers. The matchMedia check is deferred to the
      // moment we'd otherwise trigger so a preference change between
      // mount and "all approved" is picked up.
      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

      document.getElementById('post-list-column')?.scrollTo({
        top: 0,
        left: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })
      if (!prefersReducedMotion) {
        setShouldShowConfetti(true)
      }
    }
  }, [upcomingPosts, lastAction])

  // Auto-hide confetti 5 s after it appears. Owning the timer in its
  // own effect means an unrelated re-render mid-show (e.g.
  // upcomingPosts changing while confetti is up) doesn't cancel the
  // timer the way the previous combined effect did — that race left
  // confetti stuck on screen indefinitely. Cleanup runs only when
  // shouldShowConfetti flips or the component unmounts.
  useEffect(() => {
    if (!shouldShowConfetti) return
    const timer = setTimeout(() => setShouldShowConfetti(false), 5000)
    return () => clearTimeout(timer)
  }, [shouldShowConfetti])

  let navbarTabView: NavbarTabView = {
    defaultValue: tabView,
    leftItem: {
      value: 'approval' as TabViewValue,
      label: 'Approvals',
    },
    middleItem: {
      value: 'calendar' as TabViewValue,
      label: 'Calendar',
    },
    rightItem: {
      value: 'instagramGrid' as TabViewValue,
      label: 'Grid Preview',
    },
    onViewChange: (view: TabViewValue) => {
      const curTabView = tabView
      setTabView(view)
      if (view !== curTabView) {
        if (view === 'approval') {
          track('ext-approval-tab-click')
        } else if (view === 'calendar') {
          track('ext-cal-tab-click', {
            isReadOnly,
          })
        } else if (view === 'instagramGrid') {
          track('ext-planner-tab-click', {
            isReadOnly,
          })
        }
      }
    },
  }

  if (isReadOnly) {
    navbarTabView = {
      defaultValue: 'calendar',
      leftItem: {
        value: 'calendar' as TabViewValue,
        label: 'Calendar',
      },
      rightItem: {
        value: 'instagramGrid' as TabViewValue,
        label: 'Grid Preview',
      },
      onViewChange: navbarTabView.onViewChange,
    }
  }

  return (
    <div className={cn(demoCompact && 'flex h-full min-h-0 flex-col')}>
      {/* Real reviewers get the full-window burst. The demo two-pane suppresses
          it (demoCompact) because a window-wide canvas would rain over the dark
          "Your Sked" team pane, which the brand kit forbids — the demo fires its
          own client-pane-scoped confetti in DemoPayoff instead. demoCompact is
          false for real reviewers, so their behaviour is byte-identical. */}
      {shouldShowConfetti && !demoCompact && (
        <Confetti numberOfPieces={250} recycle={false} />
      )}
      {!isLoading && (
        <Navbar
          logo={setting.logoUrl}
          name={setting.companyName}
          view={navbarTabView}
        />
      )}

      {tabView === 'calendar' ? (
        <CalendarPage
          setTabView={setTabView}
          isReadOnly={isReadOnly}
          previewVariant={previewVariant}
        />
      ) : tabView === 'instagramGrid' ? (
        <InstagramGridPage
          accountSelectorProps={accountSelectorProps}
          isReadOnly={isReadOnly}
        />
      ) : isLoading ? (
        <LoadingPlaceholder previewVariant={previewVariant} />
      ) : !isLoading &&
        isEmpty(upcomingPosts.flatMap((p) => p.posts)) &&
        !isReadOnly ? (
        <EmptyUpcomingPosts />
      ) : (
        !isReadOnly && (
          <div
            className={cn(
              'bg-[#fcfcfc]',
              demoCompact && 'relative flex min-h-0 flex-1 flex-col'
            )}
          >
            <div
              className={
                demoCompact
                  ? cn(COMPACT_APPROVAL_GRID_CLASS, 'min-h-0 flex-1')
                  : getApprovalGridClassName(previewVariant)
              }
            >
              <div
                id="post-list-column"
                className={
                  demoCompact
                    ? COMPACT_LIST_COLUMN_CLASS
                    : 'relative ml-auto mt-6 h-[calc(100vh-94px)] min-w-[480px] overflow-x-auto bg-[#fcfcfc] py-0 pl-14 pr-[30px] max-[1280px]:w-full max-[1280px]:min-w-[300px] max-[1280px]:px-5'
                }
              >
                {renderTotalPendingCount(upcomingPosts) > 0 && (
                  <div className="sticky top-0 z-10 -mr-[2%] flex w-[102%] items-center pb-[10px] bg-[#fcfcfc]">
                    <div
                      className={cn(
                        TITLE_CLASS,
                        'flex w-full items-center justify-between'
                      )}
                    >
                      <span>Upcoming Posts</span>
                      <Button
                        variant="link"
                        id="pending-count"
                        onClick={handlePendingCountClick}
                        disabled={isFirstPendingSelected}
                        className="ml-[10px] h-auto shrink-0 p-0 text-sm"
                      >
                        View Pending ({renderTotalPendingCount(upcomingPosts)})
                      </Button>
                    </div>
                  </div>
                )}

                {renderTotalPendingCount(upcomingPosts) === 0 && (
                  <>
                    <h4 className={TITLE_CLASS}>Upcoming Posts</h4>
                    <div className="mb-4 mt-[10px]">
                      <NoPostToApprove />
                    </div>
                  </>
                )}

                <div id="posts-container">
                  {upcomingPosts.map(
                    ({ date, posts, id: postRowId, isVisible }) => {
                      return (
                        <div key={postRowId}>
                          <div
                            className={cn(
                              'mb-[18px] flex items-center gap-3 text-sm font-medium text-neutral-600',
                              !isVisible && 'hidden'
                            )}
                          >
                            <span className="flex shrink-0 items-center gap-2">
                              <i
                                aria-hidden="true"
                                className={cn(
                                  'text-neutral-500',
                                  date === datePlaceholderForDraftPost
                                    ? 'fa-regular fa-pen-to-square'
                                    : 'fa-regular fa-calendar'
                                )}
                              />
                              {date === datePlaceholderForDraftPost
                                ? 'Drafts'
                                : date}
                            </span>
                            <span
                              className="h-px flex-1 bg-neutral-100"
                              aria-hidden="true"
                            />
                          </div>
                          {posts.map((post) => (
                            <div
                              className={cn(
                                'mb-[18px] cursor-pointer scroll-mt-10',
                                !post.isVisible && 'hidden'
                              )}
                              key={post.id}
                              onClick={() =>
                                handleCardClick(postRowId, post.id, post.status)
                              }
                              data-postid={post.id}
                              data-postindex={post.postIndex}
                              data-post-status-pending={
                                post.status === 'pending'
                              }
                            >
                              <UpcomingPostCard
                                {...post}
                                updatePostState={updatePostState}
                              />
                            </div>
                          ))}
                        </div>
                      )
                    }
                  )}
                </div>
              </div>
              {demoCompact ? (
                // Compact preview cell: the sked preview scrolls above a fixed
                // footer that keeps the approve / request-change actions (and
                // the fa-comment drawer toggle) on screen — the drawer holds
                // the discussion, not the primary action.
                <div className={COMPACT_PREVIEW_CELL_CLASS}>
                  <div className="min-h-0 flex-1 overflow-auto px-8 pt-6 max-[1280px]:px-5">
                    <PostPreviewSection {...postPreviewSectionProps} />
                  </div>
                  <div className="flex shrink-0 items-end gap-3 border-t border-neutral-100 bg-white px-6 py-4 max-[1280px]:px-5">
                    {/* data-tour hook: the demo's lingering approve hint measures
                        this wrapper to point its arrow at the Approve control
                        (demo-compact footer only; no behaviour change). */}
                    <div
                      data-tour="approve"
                      className="min-w-0 flex-1 [&_h4]:hidden"
                    >
                      <PostAction {...compactPostActionProps} />
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsDiscussionOpen((open) => !open)}
                      aria-expanded={isDiscussionOpen}
                      aria-controls="demo-discussion-drawer"
                      className={cn(
                        'flex shrink-0 items-center gap-2 self-center rounded-lg border px-3 py-2 text-sm font-medium motion-safe:transition-colors',
                        isDiscussionOpen
                          ? 'border-blue-200 bg-blue-50 text-blue-700'
                          : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800'
                      )}
                    >
                      <i aria-hidden="true" className="fa-regular fa-comment" />
                      Discussion
                    </button>
                  </div>
                </div>
              ) : (
                <div className={getPreviewColumnClassName(previewVariant)}>
                  <PostPreviewSection {...postPreviewSectionProps} />
                </div>
              )}
              {!demoCompact && (
                <div
                  data-testid="post-action-column"
                  className="relative flex h-[calc(100vh-70px)] min-w-[480px] flex-col overflow-hidden bg-white pt-6 motion-safe:[animation:custom-show-animation_500ms_ease-in_0s_0.5] max-[1280px]:min-w-[auto] max-[1280px]:pt-5"
                >
                  {/* Actions stay padded + fixed; the conversation below fills the
                    remaining height and scrolls, with its divider lines bleeding
                    to the column edges (no column px so the lines touch left). */}
                  <div className="shrink-0 px-10 max-[1280px]:px-5">
                    <PostAction {...postActionProps} />
                    <div className="h-6" />
                  </div>
                  <Conversation {...conversationProps} />
                </div>
              )}
            </div>

            {/* Post History & Discussion drawer (demo two-pane only). Slides in
                over the right of the client frame; opacity fade, no transform,
                so the fixed confetti canvas is never re-anchored. */}
            {demoCompact && isDiscussionOpen && (
              <aside
                id="demo-discussion-drawer"
                data-testid="demo-discussion-drawer"
                className="absolute inset-y-0 right-0 z-30 flex w-[360px] max-w-[86%] flex-col border-l border-neutral-200 bg-white shadow-2xl motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
              >
                <div className="flex shrink-0 items-center justify-between border-b border-neutral-100 px-6 pb-3 pt-5">
                  <h4 className="font-gotham text-xl font-extrabold text-neutral-800">
                    Post History &amp; Discussion
                  </h4>
                  <button
                    type="button"
                    onClick={() => setIsDiscussionOpen(false)}
                    aria-label="Hide the discussion"
                    className="rounded-full p-1 text-neutral-400 motion-safe:transition-colors hover:bg-neutral-50 hover:text-neutral-700"
                  >
                    <i aria-hidden="true" className="fa-solid fa-xmark" />
                  </button>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <Conversation {...conversationProps} />
                </div>
              </aside>
            )}
          </div>
        )
      )}
    </div>
  )
}

export default App
